import { formatHumanName, MedplumClient } from "@medplum/core";
import {
  Bundle,
  BundleEntry,
  Practitioner,
  Provenance,
  Questionnaire,
  UsageContext,
} from "@medplum/fhirtypes";

import { Anchor } from "@mantine/core";
import { notifications } from "@mantine/notifications";

import * as Sentry from "@sentry/react";
import uniqBy from "lodash/uniqBy";
import { v4 as uuidv4 } from "uuid";

import { ResourceHistory } from "@/fhir/custom-types";
import { DataOperation } from "@/fhir/enums/fhir-valuesets";
import {
  FormsLibraryContext,
  FormsLibrarySection,
  QuiltedHealthCodeSystems,
} from "@/fhir/enums/quilted-health";
import { Systems } from "@/fhir/enums/systems";
import { getResourcesFromBundle, joinReferenceString } from "@/utils/medplum";
import { showSystemErrorNotification } from "@/utils/notifications";

export const updateVersion = (previousVersion: string | undefined) => {
  if (!previousVersion) return "v1";

  const newVersion = parseInt(previousVersion.slice(1), 10);
  if (isNaN(newVersion)) return "v1";
  return `v${newVersion + 1}`;
};

export const findContextBySection = (
  context: UsageContext[] | undefined,
  section: FormsLibrarySection | undefined,
): boolean => {
  if (!section) return true;
  return (
    context?.some((item) => {
      const { system, code } = item.code;
      if (system !== QuiltedHealthCodeSystems.FormsLibraryContext) return false;
      if (code !== FormsLibraryContext.Section) return false;

      const coding = item.valueCodeableConcept?.coding?.[0];

      return (
        coding?.code === section && coding?.system === QuiltedHealthCodeSystems.FormsLibrarySection
      );
    }) ?? false
  );
};

export const getProvenanceEntry = (
  targetFullUrl: string,
  activity: DataOperation,
  practitionerId: string,
  occurredDateTime: string,
): BundleEntry<Provenance> => ({
  request: { method: "POST", url: "Provenance" },
  resource: {
    resourceType: "Provenance",
    target: [{ reference: targetFullUrl }],
    recorded: occurredDateTime,
    occurredDateTime,
    agent: [{ who: { reference: joinReferenceString("Practitioner", practitionerId) } }],
    activity: {
      coding: [
        {
          system: Systems.HL7DataOperation,
          code: activity,
        },
      ],
    },
  },
});

export const getRetiredQuestionnaireEntry = (
  questionnaire: Questionnaire,
  date: string,
): BundleEntry<Questionnaire> => ({
  fullUrl: `urn:uuid:${uuidv4()}`,
  request: { method: "PUT", url: joinReferenceString("Questionnaire", questionnaire.id!) },
  resource: {
    ...questionnaire,
    status: "retired",
    date,
  },
});

export const validateCurrentQuestionnaire = async (
  medplum: MedplumClient,
  currentQuestionnaire: Questionnaire,
  url: string,
  compartment?: string[],
): Promise<boolean> => {
  const activeQuestionnaire = await medplum.searchOne("Questionnaire", {
    status: "active",
    url,
    ...(compartment ? { _compartment: compartment.join(",") } : {}),
  });

  if (!activeQuestionnaire) {
    showSystemErrorNotification();
    Sentry.captureException(new Error("Active questionnaire not found"));
    return false;
  }

  if (activeQuestionnaire.id !== currentQuestionnaire.id) {
    notifications.show({
      title: `This form version (${currentQuestionnaire.version}) is no longer active`,
      message: (
        <>
          Please use the currently active version ({activeQuestionnaire.version}){" "}
          <Anchor fz="sm" href="./" c="burgundy.9">
            here
          </Anchor>
          .
        </>
      ),
      autoClose: false,
    });
    return false;
  }

  return true;
};

export const getQuestionnairesHistory = (
  bundle: Bundle<Questionnaire | Provenance | Practitioner> | undefined,
): ResourceHistory[] => {
  const versionByQuestionnaire = (
    getResourcesFromBundle("Questionnaire", bundle) as Questionnaire[]
  )
    .filter((questionnaire) => !!questionnaire.version)
    .reduce((result, questionnaire) => {
      return {
        ...result,
        [joinReferenceString("Questionnaire", questionnaire.id!)]: [
          questionnaire.version,
          questionnaire.status!,
        ],
      };
    }, {}) as Record<string, [string, string]>;

  const nameByPractitioner = (
    getResourcesFromBundle("Practitioner", bundle) as Practitioner[]
  ).reduce((result, practitioner) => {
    const name = practitioner.name![0];
    return {
      ...result,
      [joinReferenceString("Practitioner", practitioner.id!)]: formatHumanName(name),
    };
  }, {}) as Record<string, string>;

  const provenances = uniqBy(
    (getResourcesFromBundle("Provenance", bundle) as Provenance[]).sort(
      (a, b) => new Date(b.recorded).getTime() - new Date(a.recorded).getTime(),
    ),
    (provenance) => provenance.target![0].reference,
  );

  const questionnaireHistories = provenances
    .map((provenance) => {
      const target = provenance.target?.[0]?.reference ?? "";
      const who = provenance.agent?.[0]?.who?.reference ?? "";
      const [version, status] = versionByQuestionnaire[target] ?? ["v0", "unknown"];
      const updatedBy = nameByPractitioner[who] ?? "Unknown";
      return {
        status,
        version,
        updatedBy,
        lastUpdated: provenance.occurredDateTime || provenance.recorded,
      };
    })
    .sort((a, b) => {
      if (a.status === "active" && b.status !== "active") return -1;
      if (b.status === "active" && a.status !== "active") return 1;

      return new Date(b.lastUpdated).getTime() - new Date(a.lastUpdated).getTime();
    }) as ResourceHistory[];

  return questionnaireHistories;
};

export const getTotalPages = (total: number, pageSize: number) => {
  return Math.ceil(total / pageSize);
};
